import './DescriptionEdit.css';
import { $createCodeWordNode, CodeWordNode } from './nodes/CodeWordNode';
import { $createMGRSNode, MGRSNode, mgrsToLatLon } from './nodes/MGRSNode';
import { $codeWords, $reportTemplates } from '@/stores';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  type EditorState,
  ElementNode,
  KEY_ENTER_COMMAND,
  KEY_SPACE_COMMAND,
  KEY_TAB_COMMAND,
  type LexicalEditor,
  TextNode,
} from 'lexical';
import { useEffect, useMemo, useRef, useState } from 'react';

// MGRS coordinate regex pattern - handles various spacing
// Requires at least 6 digits total (3 for easting, 3 for northing)
const MGRS_PATTERN =
  /\b(\d{1,2})\s*([A-Z])\s*([A-Z]{2})\s*(\d{3,5}(?:\s+\d{3,5})?|\d{6,10})\b/i;

// Format MGRS coordinate
function formatMGRS(text: string): string | null {
  const match = text.match(MGRS_PATTERN);
  if (!match) return null;

  const [, zone, band, square, coords] = match;

  if (!zone || !band || !square || !coords) return null;

  // Remove all spaces from coords
  const cleanCoords = coords.replace(/\s+/g, '');

  // Coords should be even length (half for easting, half for northing)
  // and at least 6 digits total (3 each for easting/northing)
  if (cleanCoords.length % 2 !== 0 || cleanCoords.length < 6) return null;

  const mid = cleanCoords.length / 2;
  const easting = cleanCoords.slice(0, mid);
  const northing = cleanCoords.slice(mid);

  return `${zone.toUpperCase()}${band.toUpperCase()} ${square.toUpperCase()} ${easting} ${northing}`;
}

/**
 * Extracts phrases up to cursor. E.g.:
 * Input: "lorem ipsum dolar sit amet", 21
 * Output: [
 *   { phrase: "sit", span: [18, 21] },
 *   { phrase: "dolar sit", span: [12, 21] },
 *   { phrase: "ipsum dolar sit", span: [6, 21] },
 *   { phrase: "lorem ipsum dolar sit", span: [0, 21] },
 * ]
 */
function* extractPhrasesToCursor(
  text: string,
  offset: number,
): Generator<{
  phrase: string;
  span: [number, number];
}> {
  const textBefore = text.slice(0, offset);
  const words = textBefore.split(/\s+/);

  for (let i = words.length - 1; i >= 0; i--) {
    const phrase = words.slice(i).join(' ');
    yield { phrase, span: [offset - phrase.length, offset] };
  }
}

// Extract potential MGRS coordinate before cursor (can include spaces)
function getMGRSBeforeCursor(
  text: string,
  offset: number,
): { word: string; start: number } | null {
  // Look back up to 30 characters for MGRS pattern
  const lookbackStart = Math.max(0, offset - 30);
  const textBefore = text.slice(lookbackStart, offset);

  // Try to find MGRS pattern ending at cursor position
  // Requires at least 6 digits total (3 for easting, 3 for northing)
  const mgrsPattern =
    /(\d{1,2}\s*[A-Z]\s*[A-Z]{2}\s*(?:\d{3,5}(?:\s+\d{3,5})?|\d{6,10}))$/i;
  const match = textBefore.match(mgrsPattern);

  if (match && match[1]) {
    const start = lookbackStart + match.index!;
    return { word: match[1], start };
  }

  return null;
}

// Find all placeholders in text
function findPlaceholders(): Array<{
  node: TextNode;
  start: number;
  end: number;
  text: string;
}> {
  const placeholders: Array<{
    node: TextNode;
    start: number;
    end: number;
    text: string;
  }> = [];
  const regex = /<[^>]+>/g;

  const addPlaceholdersFromNode = (node: ElementNode) => {
    node.getChildren().forEach(child => {
      if (child instanceof TextNode) {
        const nodeText = child.getTextContent();
        let match;

        while ((match = regex.exec(nodeText)) !== null) {
          placeholders.push({
            node: child,
            start: match.index,
            end: match.index + match[0].length,
            text: match[0],
          });
        }
      } else if (child instanceof ElementNode) {
        addPlaceholdersFromNode(child);
      }
    });
  };

  addPlaceholdersFromNode($getRoot());

  return placeholders;
}

// Plugin for handling MGRS formatting, templates, and tab navigation
function EditorPlugin({
  onPlaceholderNav,
  placeholders,
  onSave,
  readonly,
  reportTemplates,
}: {
  onPlaceholderNav: () => void;
  placeholders: Array<{ start: number; end: number; text: string }>;
  onSave?: () => void;
  readonly?: boolean;
  reportTemplates: Record<string, string>;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Don't register commands in readonly mode
    if (readonly) {
      return () => {};
    }

    // Shared function to handle MGRS formatting
    const tryFormatMGRS = (): boolean => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return false;

      const anchor = selection.anchor;
      const node = anchor.getNode();

      if (node instanceof TextNode) {
        const text = node.getTextContent();
        const offset = anchor.offset;

        // First try to find MGRS coordinate (which can have spaces)
        const mgrsMatch = getMGRSBeforeCursor(text, offset);

        if (mgrsMatch) {
          const { word, start } = mgrsMatch;
          const formatted = formatMGRS(word);

          if (formatted) {
            // Format in place, cursor will be positioned after formatted text
            const newText =
              text.slice(0, start) + formatted + text.slice(offset);
            node.setTextContent(newText);

            // Move cursor to after the formatted text
            const newOffset = start + formatted.length;
            selection.anchor.set(node.getKey(), newOffset, 'text');
            selection.focus.set(node.getKey(), newOffset, 'text');

            return true;
          }
        }
      }

      return false;
    };

    // Handle SPACE key for MGRS formatting
    const removeSpaceCommand = editor.registerCommand(
      KEY_SPACE_COMMAND,
      (event: KeyboardEvent) => {
        editor.update(() => {
          if (tryFormatMGRS()) {
            // Formatting happened, don't prevent default - let space be inserted
          }
        });
        return false; // Always allow default space behavior
      },
      COMMAND_PRIORITY_HIGH,
    );

    // Handle ENTER key for MGRS formatting or template expansion
    const removeEnterCommand = editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event: KeyboardEvent) => {
        // Skip if Ctrl/Cmd is pressed (handled by save command)
        if (event && (event.ctrlKey || event.metaKey)) {
          return false;
        }

        let mgrsFormatted = false;
        editor.update(() => {
          mgrsFormatted = tryFormatMGRS();
        });

        // If MGRS was formatted, allow default enter behavior
        if (mgrsFormatted) {
          return false;
        }

        // Try template expansion
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;

        const anchor = selection.anchor;
        const node = anchor.getNode();

        if (node instanceof TextNode) {
          const text = node.getTextContent();
          const offset = anchor.offset;
          const phrases = extractPhrasesToCursor(text, offset);

          let template: string | undefined;
          let phrase:
            | {
                phrase: string;
                span: [number, number];
              }
            | undefined;
          for (const p of phrases) {
            template = reportTemplates[p.phrase.toUpperCase()];
            phrase = p;
            if (template) {
              break;
            }
          }

          if (!template || !phrase) {
            return false;
          }

          const spaces = text.match(/^\s+/g)?.[0] ?? '';

          event?.preventDefault();

          editor.update(() => {
            const lines = template.split('\n'); // Split template into lines

            let insertNode = node;

            if (phrase!.span[0] !== 0 || phrase!.span[1] !== text.length) {
              const nodes = node.splitText(phrase!.span[0], phrase!.span[1]);
              insertNode = nodes[1]!;
            }

            lines.forEach((line, index) => {
              insertNode.insertBefore($createTextNode(spaces + line));
              if (index < lines.length - 1) {
                insertNode.insertBefore($createLineBreakNode());
              }
            });

            insertNode.remove();

            // Find and select the first placeholder
            const placeholders = findPlaceholders();
            if (placeholders.length > 0) {
              const firstPlaceholder = placeholders[0]!;
              selection.anchor.set(
                firstPlaceholder.node.getKey(),
                firstPlaceholder.start,
                'text',
              );
              selection.focus.set(
                firstPlaceholder.node.getKey(),
                firstPlaceholder.end,
                'text',
              );
            }
          });

          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    // Handle TAB key for MGRS formatting or placeholder navigation
    const removeTabCommand = editor.registerCommand(
      KEY_TAB_COMMAND,
      (event: KeyboardEvent) => {
        // Try placeholder navigation
        const placeholders = findPlaceholders();
        if (placeholders.length === 0) return false;

        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;

        const anchor = selection.anchor;
        const focus = selection.focus;

        // Determine the furthest node and offset in the selection
        let furthestNode = anchor.getNode();
        let furthestOffset = anchor.offset;

        if (focus.getNode() !== anchor.getNode()) {
          // Compare nodes by their keys to determine the furthest node
          if (focus.getNode().getKey() > anchor.getNode().getKey()) {
            furthestNode = focus.getNode();
            furthestOffset = focus.offset;
          }
        } else if (focus.offset > anchor.offset) {
          // If nodes are the same, compare offsets
          furthestOffset = focus.offset;
        }

        let targetPlaceholder;

        if (event.shiftKey) {
          // Find the previous placeholder where the selection's furthest node and offset is to the right
          targetPlaceholder = [...placeholders].reverse().find(p => {
            if (p.node === furthestNode) {
              // Compare offsets if nodes are the same
              return p.end < furthestOffset;
            }
            // Compare nodes if they are different
            return Number(p.node.getKey()) < Number(furthestNode.getKey());
          });
        } else {
          // Find the next placeholder where the selection's furthest node and offset is to the left
          targetPlaceholder = placeholders.find(p => {
            if (p.node === furthestNode) {
              // Compare offsets if nodes are the same
              return p.start > furthestOffset;
            }
            // Compare nodes if they are different
            return Number(p.node.getKey()) > Number(furthestNode.getKey());
          });
        }

        if (!targetPlaceholder) return false;

        event.preventDefault();

        editor.update(() => {
          // Select the placeholder directly using the TextNode and relative offsets
          selection.anchor.set(
            targetPlaceholder.node.getKey(),
            targetPlaceholder.start,
            'text',
          );
          selection.focus.set(
            targetPlaceholder.node.getKey(),
            targetPlaceholder.end,
            'text',
          );

          onPlaceholderNav();
        });

        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );

    // Handle Ctrl/Cmd+Enter for save action
    const removeEnterSaveCommand = onSave
      ? editor.registerCommand(
          KEY_ENTER_COMMAND,
          (event: KeyboardEvent) => {
            if (event && (event.ctrlKey || event.metaKey)) {
              event.preventDefault();
              onSave();
              return true;
            }
            return false;
          },
          COMMAND_PRIORITY_HIGH,
        )
      : () => {};

    return () => {
      removeSpaceCommand();
      removeEnterCommand();
      removeTabCommand();
      removeEnterSaveCommand();
    };
  }, [
    editor,
    onPlaceholderNav,
    placeholders,
    onSave,
    readonly,
    reportTemplates,
  ]);

  return null;
}

// Plugin to handle blur events
function BlurPlugin({ onBlur }: { onBlur?: () => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!onBlur) return;

    return editor.registerRootListener((rootElement, prevRootElement) => {
      if (prevRootElement) {
        prevRootElement.removeEventListener('blur', onBlur);
      }
      if (rootElement) {
        rootElement.addEventListener('blur', onBlur);
      }
    });
  }, [editor, onBlur]);

  return null;
}

// Plugin to sync editor state with external state
function SyncPlugin({
  description,
  setDescription,
  onTextChange,
}: {
  description: string;
  setDescription?: (value: string) => void;
  onTextChange: (text: string) => void;
}) {
  const [editor] = useLexicalComposerContext();

  // Sync external changes to editor
  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();
      const currentText = root.getTextContent();

      if (currentText !== description) {
        root.clear();
        const paragraph = $createParagraphNode();

        const lines = description.split('\n');

        lines.forEach((line, index) => {
          paragraph.append($createTextNode(line));
          if (index < lines.length - 1) {
            paragraph.append($createLineBreakNode());
          }
        });

        root.append(paragraph);
      }
    });
  }, [description, editor]);

  const handleChange = (editorState: EditorState, editor: LexicalEditor) => {
    if (!setDescription) return;
    editorState.read(() => {
      const root = $getRoot();
      const text = root.getTextContent();

      setDescription(text);
      onTextChange(text);
    });
  };

  return <OnChangePlugin onChange={handleChange} />;
}

// Plugin to highlight code words and show tooltips on hover
function CodeWordPlugin({ codeWords }: { codeWords: Record<string, string> }) {
  const [editor] = useLexicalComposerContext();

  const { regex, meaningMap } = useMemo(() => {
    const keys = Object.keys(codeWords || {});
    const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const upperMap = keys.reduce<Record<string, string>>((acc, key) => {
      acc[key.toUpperCase()] = codeWords[key]!;
      return acc;
    }, {});
    const pattern =
      keys.length > 0
        ? new RegExp(`\\b(${keys.map(escape).join('|')})\\b`, 'gi')
        : null;
    return { regex: pattern, meaningMap: upperMap };
  }, [codeWords]);

  useEffect(() => {
    if (!regex) return;

    return editor.registerNodeTransform(TextNode, node => {
      if (node instanceof CodeWordNode || node instanceof MGRSNode) return;

      let currentNode: TextNode = node;
      let text = currentNode.getTextContent();

      if (!regex.test(text)) return;
      regex.lastIndex = 0;

      let match: RegExpExecArray | null;

      while ((match = regex.exec(text)) !== null) {
        const matchedText = match[0]!;
        const start = match.index;
        const end = start + matchedText.length;

        const [left, middle, right] = currentNode.splitText(start, end);
        const meaning = meaningMap[matchedText.toUpperCase()] ?? matchedText;

        if (middle && middle.getTextContent() === matchedText) {
          middle.replace($createCodeWordNode(meaning, matchedText));
        } else if (left && left.getTextContent() === matchedText) {
          left.replace($createCodeWordNode(meaning, matchedText));
        } else if (right && right.getTextContent() === matchedText) {
          right.replace($createCodeWordNode(meaning, matchedText));
        }

        if (!right) break;

        currentNode = right;
        text = currentNode.getTextContent();
        regex.lastIndex = 0;
      }
    });
  }, [editor, regex, meaningMap]);

  return null;
}

// Plugin to transform MGRS coordinates into clickable links
function MGRSPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerNodeTransform(TextNode, node => {
      if (node instanceof CodeWordNode || node instanceof MGRSNode) return;

      const text = node.getTextContent();
      // Match formatted MGRS patterns like "19 T CN 02313 42183" or "19TCN02313 42183"
      const mgrsRegex =
        /\b(\d{1,2}\s*[A-Z]\s*[A-Z]{2}\s*\d{3,5}\s+\d{3,5})\b/gi;

      let match;
      let offset = 0;

      while ((match = mgrsRegex.exec(text)) !== null) {
        const mgrsString = match[0]!;
        const start = match.index;
        const end = start + mgrsString.length;

        const coords = mgrsToLatLon(mgrsString);
        if (!coords) continue;

        const [left, middle, right] = node.splitText(start, end);

        if (middle && middle.getTextContent() === mgrsString) {
          middle.replace($createMGRSNode(coords[0], coords[1], mgrsString));
        } else if (left && left.getTextContent() === mgrsString) {
          left.replace($createMGRSNode(coords[0], coords[1], mgrsString));
        } else if (right && right.getTextContent() === mgrsString) {
          right.replace($createMGRSNode(coords[0], coords[1], mgrsString));
        }

        if (!right) break;
        node = right;
      }
    });
  }, [editor]);

  return null;
}

interface DescriptionEditProps {
  readonly?: boolean;
  description: string;
  setDescription?: (value: string) => void;
  placeholder?: string;
  onSave?: () => void;
  onBlur?: () => void;
}

export default function DescriptionEdit({
  readonly = false,
  description,
  setDescription,
  placeholder = 'Enter description... (try SITREP, MEDEVAC, or MGRS coords like 19tcn0231342183)',
  onSave,
  onBlur,
}: DescriptionEditProps) {
  const reportTemplates = useStore($reportTemplates);
  const codeWords = useStore($codeWords);
  const contentEditableRef = useRef<HTMLDivElement | null>(null);
  const [placeholders, setPlaceholders] = useState<
    Array<{ start: number; end: number; text: string }>
  >([]);
  const [isTabMode, setIsTabMode] = useState(false);

  const handlePlaceholderNav = () => {
    setIsTabMode(true);
  };

  const handleTextChange = () => {
    const newPlaceholders = findPlaceholders();
    setPlaceholders(newPlaceholders);

    // Exit tab mode if no placeholders or on any click
    if (newPlaceholders.length === 0) {
      setIsTabMode(false);
    }
  };

  const handleClick = () => {
    // Clicking exits tab navigation mode
    setIsTabMode(false);
  };

  const initialConfig = {
    namespace: 'DescriptionEditor',
    theme: {
      paragraph: 'mb-0',
    },
    nodes: [CodeWordNode, MGRSNode],
    onError: (error: Error) => {
      console.error(error);
    },
  };

  return (
    <div className="relative w-full" onClick={handleClick}>
      <LexicalComposer initialConfig={initialConfig}>
        <div className="relative">
          <CodeWordPlugin codeWords={codeWords} />
          <MGRSPlugin />
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                ref={contentEditableRef as any}
                className={clsx(
                  'min-h-10 resize-none px-3 py-2',
                  !readonly &&
                    'focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none',
                  readonly && 'text-inherit',
                )}
                style={Object.assign(
                  {
                    fontFamily: 'inherit',
                    fontSize: 'inherit',
                    lineHeight: '1.5',
                  },
                  !readonly ? { caretColor: 'black' } : {},
                )}
                contentEditable={!readonly}
                aria-readonly={readonly}
              />
            }
            placeholder={
              <div className="pointer-events-none absolute top-2 right-3 bottom-2 left-3 overflow-hidden overflow-ellipsis whitespace-nowrap text-gray-400 select-none">
                {!readonly && placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <SyncPlugin
            description={description}
            setDescription={setDescription}
            onTextChange={handleTextChange}
          />
          <BlurPlugin onBlur={onBlur} />
          <EditorPlugin
            onPlaceholderNav={handlePlaceholderNav}
            placeholders={placeholders}
            onSave={onSave}
            readonly={readonly}
            reportTemplates={reportTemplates}
          />
        </div>
      </LexicalComposer>

      {/* Helper text */}
      <div className="text-xs text-gray-500 print:hidden">
        {!readonly && isTabMode && placeholders.length > 0 && (
          <span>
            Press Tab to navigate placeholders ({placeholders.length} remaining)
          </span>
        )}
      </div>
    </div>
  );
}
